import { DEMO_SCENARIOS, type DemoScenario } from "@wayfind/core";

/**
 * Starting a run with no infrastructure at all.
 *
 * The publisher is a second mesh node in this tab driving a real plan over
 * the loopback transport: real PMT1 records, sealed and signed, validated
 * by the same subscriber pipeline a phone across town would use. The only
 * thing that is not real is the van. The banner says so on every screen,
 * because a demonstration a person could mistake for their actual delivery
 * is worse than no demonstration.
 */
export function DemoPicker({
  onPick,
  busy,
  ready
}: {
  onPick: (scenario: DemoScenario) => void;
  busy: string | null;
  /**
   * Whether the engine session is open yet.
   *
   * The buttons used to be live from first paint, while `startDemo` began
   * with `if (!session) return`. A click in that window did nothing at all
   * — no run, no spinner, no message — and the page looked broken in the
   * one way a visitor cannot tell from "this product does not work".
   */
  ready: boolean;
}): JSX.Element {
  const waiting = !ready;
  return (
    <section className="panel">
      <p className="eyebrow">Nothing to follow yet</p>
      <div className="stack">
        <p style={{ margin: 0, color: "var(--ink-soft)" }}>
          A tracking link opens straight into the run it belongs to. This page was
          opened without one — so you can watch a demonstration instead.
        </p>
        <div className="picker">
          {DEMO_SCENARIOS.map(scenario => (
            <button
              key={scenario.id}
              type="button"
              className="picker__option"
              onClick={() => onPick(scenario)}
              disabled={busy != null || waiting}
              aria-pressed={busy === scenario.id}
              aria-label={`Watch a demonstration: ${scenario.title}. ${scenario.blurb}`}
            >
              <span className="picker__title">{scenario.title}</span>
              <span className="picker__blurb">{scenario.blurb}</span>
            </button>
          ))}
        </div>
        {waiting ? (
          <p className="row-quiet">
            <span className="spinner" aria-hidden="true" />
            Loading the map…
          </p>
        ) : busy ? (
          <p className="row-quiet">
            <span className="spinner" aria-hidden="true" />
            Starting the run…
          </p>
        ) : null}
      </div>
    </section>
  );
}
